import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import {
  Button,
  DateInput,
  GeneralList,
  GeneralListSortableTh,
  Label,
  NumberInput,
  Select,
  TextInput,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { PencilIcon, PlusIcon, TrashIcon, UserIcon, XIcon } from '../../components/ui/Icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../components/ui/Modal';
import { CurrencySelect } from '../../components/ui/dropdowns';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import {
  createMgaBondedLine,
  createMgaCashEntry,
  createMgaCrewRequest,
  deleteMgaBondedLine,
  deleteMgaCashEntry,
  deleteMgaCrewRequest,
  updateMgaBondedLine,
  updateMgaCashEntry,
  updateMgaCrewRequest,
} from '../../services/mutations';
import {
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaCrewRequests,
  type MgaBondedLine,
  type MgaCashEntry,
  type MgaCashEntryType,
  type MgaCrewLine,
  type MgaCrewRequest,
  type MgaSettlementColumnDef,
} from './useData';
import { isStoreOnlyStockOut } from './closeChecklist';
import { bondedPayrollColumnLabel, BONDED_PAYROLL_COLUMN_OPTIONS } from './settlementColumns';
import {
  BONDED_EXPENSE_CODE,
  BONDED_INCOMING_CODE,
  buildBooksSidebarGroups,
  buildBooksSidebarItems,
  CASH_EXPENSE_CODE,
  CASH_INCOME_CODE,
  isBondedViewCode,
  isCashViewCode,
  requestCategoriesFromDefs,
  requestColumnCode,
  type BooksSidebarItem as SidebarItem,
} from './booksCategories';

export {
  BONDED_EXPENSE_CODE,
  BONDED_INCOMING_CODE,
  CASH_EXPENSE_CODE,
  CASH_INCOME_CODE,
} from './booksCategories';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(n: number | null | undefined) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function cashAmount(e: Pick<MgaCashEntry, 'amount' | 'amountPeriod'>) {
  return Number(e.amountPeriod ?? e.amount) || 0;
}

function BooksTableShell({
  children,
  footer,
}: {
  children: React.ReactNode;
  footer: React.ReactNode | null;
}) {
  return (
    <div className="flex min-h-full flex-col">
      <div className="min-w-0 flex-1">{children}</div>
      {footer}
    </div>
  );
}

export function BooksTab({
  periodId,
  editable,
  canEdit,
  lines,
  currency = 'USD',
  categories,
  activeCategory,
  onCategoryChange,
  onChanged,
}: {
  periodId: number;
  editable: boolean;
  canEdit: boolean;
  lines: MgaCrewLine[];
  currency?: string;
  categories: MgaSettlementColumnDef[];
  activeCategory: string;
  onCategoryChange: (code: string) => void;
  onChanged?: () => void | Promise<unknown>;
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const confirm = useConfirm();
  const { data: requests, mutate: mutateRequests, isLoading: requestsLoading } =
    useMgaCrewRequests(periodId);
  const { data: bonded, mutate: mutateBonded, isLoading: bondedLoading } =
    useMgaBondedLines(periodId);
  const { data: cash, mutate: mutateCash, isLoading: cashLoading } = useMgaCashEntries(periodId);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const sort = useGeneralListSort({ key: 'entryDate', direction: 'asc' });

  const requestCategories = useMemo(
    () => requestCategoriesFromDefs(categories),
    [categories],
  );

  const sidebarItems = useMemo(
    () => buildBooksSidebarItems(categories),
    [categories],
  );

  const sidebarGroups = useMemo(
    () => buildBooksSidebarGroups(sidebarItems),
    [sidebarItems],
  );

  const selected =
    sidebarItems.find((c) => c.code === activeCategory) ?? sidebarItems[0] ?? null;
  const isBondedView = Boolean(selected && isBondedViewCode(selected.code));
  const isBondedExpense = selected?.code === BONDED_EXPENSE_CODE;
  const isCashView = Boolean(selected && isCashViewCode(selected.code));
  const isCashExpense = selected?.code === CASH_EXPENSE_CODE;

  useEffect(() => {
    if (!selected) return;
    if (selected.code !== activeCategory) onCategoryChange(selected.code);
  }, [selected, activeCategory, onCategoryChange]);

  const activeItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const el = activeItemRef.current;
    if (!el) return;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selected?.code]);

  const [requestForm, setRequestForm] = useState({
    columnCode: 'allotmentAmount',
    entryDate: todayIso(),
    mgaCrewLineId: '',
    amount: '',
    remarks: '',
  });

  const [bondedForm, setBondedForm] = useState({
    entryDate: todayIso(),
    description: '',
    unitPrice: '',
    qty: '',
    mgaCrewLineId: '',
    payrollColumnCode: '',
    remarks: '',
  });

  const [cashForm, setCashForm] = useState({
    entryType: 'INCOME' as MgaCashEntryType,
    entryDate: todayIso(),
    description: '',
    amount: '',
    currency: 'USD',
    exchangeRate: '1',
    remarks: '',
    mgaCrewLineId: '',
  });

  const closeForm = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const openRequestForm = (columnCode?: string) => {
    const code = columnCode || selected?.code || 'allotmentAmount';
    setEditingId(null);
    setRequestForm({
      columnCode: code,
      entryDate: todayIso(),
      mgaCrewLineId: lines[0] ? String(lines[0].id) : '',
      amount: '',
      remarks: '',
    });
    setFormOpen(true);
  };

  const openBondedForm = () => {
    setEditingId(null);
    setBondedForm({
      entryDate: todayIso(),
      description: '',
      unitPrice: '',
      qty: '',
      mgaCrewLineId: isBondedExpense && lines[0] ? String(lines[0].id) : '',
      payrollColumnCode: isBondedExpense ? 'otherBondedAmount' : '',
      remarks: '',
    });
    setFormOpen(true);
  };

  const openCashForm = () => {
    setEditingId(null);
    setCashForm({
      entryType: isCashExpense ? 'EXPENSE' : 'INCOME',
      entryDate: todayIso(),
      description: '',
      amount: '',
      currency: currency || 'USD',
      exchangeRate: '1',
      remarks: '',
      mgaCrewLineId: '',
    });
    setFormOpen(true);
  };

  const openEditRequest = (row: MgaCrewRequest) => {
    if (!canEdit || !editable) return;
    if (row.fromStockOut || row.mgaBondedLineId) {
      showError('This charge comes from Stock out. Edit it under Bonded store → Stock out.');
      return;
    }
    setEditingId(row.id);
    setRequestForm({
      columnCode: requestColumnCode(row) || selected?.code || 'allotmentAmount',
      entryDate: row.entryDate ? String(row.entryDate).slice(0, 10) : todayIso(),
      mgaCrewLineId: row.mgaCrewLineId != null ? String(row.mgaCrewLineId) : '',
      amount: row.amount != null ? String(row.amount) : '',
      remarks: row.remarks || '',
    });
    setFormOpen(true);
  };

  const openEditBonded = (row: MgaBondedLine) => {
    if (!canEdit || !editable) return;
    setEditingId(row.id);
    setBondedForm({
      entryDate: row.entryDate ? String(row.entryDate).slice(0, 10) : todayIso(),
      description: row.description || '',
      unitPrice: row.unitPrice != null ? String(row.unitPrice) : '',
      qty: row.qty != null ? String(row.qty) : '',
      mgaCrewLineId: row.mgaCrewLineId != null ? String(row.mgaCrewLineId) : '',
      payrollColumnCode: row.payrollColumnCode || '',
      remarks: row.remarks || '',
    });
    setFormOpen(true);
  };

  const openEditCash = (row: MgaCashEntry) => {
    if (!canEdit || !editable) return;
    setEditingId(row.id);
    setCashForm({
      entryType: row.entryType,
      entryDate: row.entryDate ? String(row.entryDate).slice(0, 10) : todayIso(),
      description: row.description || '',
      amount: row.amount != null ? String(row.amount) : '',
      currency: row.currency || 'USD',
      exchangeRate: row.exchangeRate != null ? String(row.exchangeRate) : '1',
      remarks: row.remarks || '',
      mgaCrewLineId: row.mgaCrewLineId != null ? String(row.mgaCrewLineId) : '',
    });
    setFormOpen(true);
  };

  const categoryStats = useMemo(() => {
    const map = new Map<string, { count: number; total: number }>();
    for (const item of sidebarItems) map.set(item.code, { count: 0, total: 0 });

    for (const r of requests) {
      const code = requestColumnCode(r);
      if (!code || !map.has(code)) continue;
      const cur = map.get(code)!;
      cur.count += 1;
      cur.total = Math.round((cur.total + Number(r.amount || 0)) * 100) / 100;
    }

    const incoming = bonded.filter((l) => l.section === 'STOCK' || l.section === 'INCOMING');
    const expense = bonded.filter((l) => l.section === 'EXPENSE');
    if (map.has(BONDED_INCOMING_CODE)) {
      map.set(BONDED_INCOMING_CODE, {
        count: incoming.length,
        total: Math.round(incoming.reduce((s, r) => s + Number(r.amount || 0), 0) * 100) / 100,
      });
    }
    if (map.has(BONDED_EXPENSE_CODE)) {
      map.set(BONDED_EXPENSE_CODE, {
        count: expense.length,
        total: Math.round(expense.reduce((s, r) => s + Number(r.amount || 0), 0) * 100) / 100,
      });
    }

    const cashIncome = cash.filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM');
    const cashExpense = cash.filter((e) => e.entryType === 'EXPENSE');
    if (map.has(CASH_INCOME_CODE)) {
      map.set(CASH_INCOME_CODE, {
        count: cashIncome.length,
        total: Math.round(cashIncome.reduce((s, r) => s + cashAmount(r), 0) * 100) / 100,
      });
    }
    if (map.has(CASH_EXPENSE_CODE)) {
      map.set(CASH_EXPENSE_CODE, {
        count: cashExpense.length,
        total: Math.round(cashExpense.reduce((s, r) => s + cashAmount(r), 0) * 100) / 100,
      });
    }
    return map;
  }, [requests, bonded, cash, sidebarItems]);

  const categoryMatchCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, number>();
    for (const item of sidebarItems) map.set(item.code, 0);
    if (!q) return map;

    for (const r of requests) {
      const code = requestColumnCode(r);
      if (!code || !map.has(code)) continue;
      const hay = [r.crewLine?.crewName, r.remarks, r.columnCode, r.requestType, String(r.amount ?? '')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (hay.includes(q)) map.set(code, (map.get(code) ?? 0) + 1);
    }
    for (const l of bonded) {
      const code =
        l.section === 'EXPENSE' ? BONDED_EXPENSE_CODE : BONDED_INCOMING_CODE;
      if (!map.has(code)) continue;
      const hay = [l.description, l.remarks, l.crewLine?.crewName, String(l.amount ?? '')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (hay.includes(q)) map.set(code, (map.get(code) ?? 0) + 1);
    }
    for (const e of cash) {
      const code = e.entryType === 'EXPENSE' ? CASH_EXPENSE_CODE : CASH_INCOME_CODE;
      if (!map.has(code)) continue;
      const hay = [
        e.description,
        e.remarks,
        e.entryType,
        e.crewLine?.crewName,
        String(e.amountPeriod ?? e.amount ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (hay.includes(q)) map.set(code, (map.get(code) ?? 0) + 1);
    }
    return map;
  }, [search, requests, bonded, cash, sidebarItems]);

  const booksSearchHitTotal = useMemo(() => {
    let n = 0;
    categoryMatchCounts.forEach((v) => {
      n += v;
    });
    return n;
  }, [categoryMatchCounts]);

  const booksSearchCategoryHits = useMemo(() => {
    let n = 0;
    categoryMatchCounts.forEach((v) => {
      if (v > 0) n += 1;
    });
    return n;
  }, [categoryMatchCounts]);

  useEffect(() => {
    const q = search.trim();
    if (!q || !selected) return;
    const currentHits = categoryMatchCounts.get(selected.code) ?? 0;
    if (currentHits > 0) return;
    const first = sidebarItems.find((i) => (categoryMatchCounts.get(i.code) ?? 0) > 0);
    if (first) onCategoryChange(first.code);
  }, [search, selected, categoryMatchCounts, sidebarItems, onCategoryChange]);

  const requestRows = useMemo(() => {
    if (!selected || selected.kind !== 'request') return [];
    return requests.filter((r) => requestColumnCode(r) === selected.code);
  }, [requests, selected]);

  const bondedRows = useMemo(() => {
    if (!selected || selected.kind !== 'bonded') return [];
    if (selected.code === BONDED_EXPENSE_CODE) {
      return bonded.filter((l) => l.section === 'EXPENSE');
    }
    return bonded.filter((l) => l.section === 'STOCK' || l.section === 'INCOMING');
  }, [bonded, selected]);

  const cashRows = useMemo(() => {
    if (!selected || selected.kind !== 'cash') return [];
    if (selected.code === CASH_EXPENSE_CODE) {
      return cash.filter((e) => e.entryType === 'EXPENSE');
    }
    return cash.filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM');
  }, [cash, selected]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = requestRows;
    if (q) {
      list = list.filter((e) => {
        const hay = [e.crewLine?.crewName, e.remarks, e.columnCode, e.requestType]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sort.sortConfig.direction === 'asc' ? 1 : -1;
    const key = sort.sortConfig.key;
    return [...list].sort((a, b) => {
      if (key === 'amount') return (Number(a.amount) - Number(b.amount)) * dir;
      if (key === 'crewName') {
        return (
          String(a.crewLine?.crewName ?? '').localeCompare(String(b.crewLine?.crewName ?? ''), undefined, {
            sensitivity: 'base',
          }) * dir
        );
      }
      const ad = a.entryDate ? String(a.entryDate) : '';
      const bd = b.entryDate ? String(b.entryDate) : '';
      return ad.localeCompare(bd) * dir || a.id - b.id;
    });
  }, [requestRows, search, sort.sortConfig]);

  const filteredBonded = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = bondedRows;
    if (q) {
      list = list.filter((e) => {
        const hay = [e.description, e.remarks, e.crewLine?.crewName, e.section]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sort.sortConfig.direction === 'asc' ? 1 : -1;
    const key = sort.sortConfig.key;
    return [...list].sort((a, b) => {
      if (key === 'amount' || key === 'unitPrice' || key === 'qty') {
        const av = Number(a[key as 'amount' | 'unitPrice' | 'qty']) || 0;
        const bv = Number(b[key as 'amount' | 'unitPrice' | 'qty']) || 0;
        return (av - bv) * dir;
      }
      if (key === 'crewName') {
        return (
          String(a.crewLine?.crewName ?? '').localeCompare(String(b.crewLine?.crewName ?? ''), undefined, {
            sensitivity: 'base',
          }) * dir
        );
      }
      if (key === 'description') {
        return String(a.description ?? '').localeCompare(String(b.description ?? '')) * dir;
      }
      const ad = a.entryDate ? String(a.entryDate) : '';
      const bd = b.entryDate ? String(b.entryDate) : '';
      return ad.localeCompare(bd) * dir || a.id - b.id;
    });
  }, [bondedRows, search, sort.sortConfig]);

  const filteredCash = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = cashRows;
    if (q) {
      list = list.filter((e) => {
        const hay = [e.description, e.entryType, e.currency, e.remarks, e.crewLine?.crewName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sort.sortConfig.direction === 'asc' ? 1 : -1;
    const key = sort.sortConfig.key;
    return [...list].sort((a, b) => {
      if (key === 'amount' || key === 'amountPeriod') {
        const av = cashAmount(a);
        const bv = cashAmount(b);
        return (av - bv) * dir;
      }
      if (key === 'crewName' || key === 'crewLine') {
        return (
          String(a.crewLine?.crewName ?? '').localeCompare(String(b.crewLine?.crewName ?? ''), undefined, {
            sensitivity: 'base',
          }) * dir
        );
      }
      if (key === 'description') {
        return String(a.description ?? '').localeCompare(String(b.description ?? '')) * dir;
      }
      if (key === 'entryType') {
        return String(a.entryType).localeCompare(String(b.entryType)) * dir;
      }
      const ad = a.entryDate ? String(a.entryDate) : '';
      const bd = b.entryDate ? String(b.entryDate) : '';
      return ad.localeCompare(bd) * dir || a.id - b.id;
    });
  }, [cashRows, search, sort.sortConfig]);

  const activeFilteredCount = isCashView
    ? filteredCash.length
    : isBondedView
      ? filteredBonded.length
      : filteredRequests.length;
  const totalPages = Math.max(1, Math.ceil(activeFilteredCount / itemsPerPage) || 1);

  useEffect(() => setPage(1), [
    search,
    itemsPerPage,
    selected?.code,
    sort.sortConfig.key,
    sort.sortConfig.direction,
  ]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRequestRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredRequests.slice(start, start + itemsPerPage);
  }, [filteredRequests, page, itemsPerPage]);

  const pageBondedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredBonded.slice(start, start + itemsPerPage);
  }, [filteredBonded, page, itemsPerPage]);

  const pageCashRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredCash.slice(start, start + itemsPerPage);
  }, [filteredCash, page, itemsPerPage]);

  const categoryTotal = categoryStats.get(selected?.code ?? '')?.total ?? 0;
  const storeOnlyStockOuts = useMemo(
    () => (isBondedExpense ? bonded.filter(isStoreOnlyStockOut) : []),
    [isBondedExpense, bonded],
  );
  const listRowCount = isCashView
    ? cashRows.length
    : isBondedView
      ? bondedRows.length
      : requestRows.length;
  const isLoading = isCashView ? cashLoading : isBondedView ? bondedLoading : requestsLoading;
  const hasSearch = search.trim().length > 0;

  const saveRequest = async () => {
    if (!canEdit || !editable) return;
    const mgaCrewLineId = Number(requestForm.mgaCrewLineId);
    const amount = Number(requestForm.amount);
    if (!Number.isFinite(mgaCrewLineId) || mgaCrewLineId <= 0) {
      showError('Select a crew member.');
      return;
    }
    if (!Number.isFinite(amount)) {
      showError('Enter a valid amount.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        columnCode: requestForm.columnCode,
        mgaCrewLineId,
        entryDate: requestForm.entryDate || null,
        amount,
        remarks: requestForm.remarks.trim() || null,
      };
      if (editingId != null) {
        await updateMgaCrewRequest(periodId, editingId, payload);
        showSuccess('Deduction line updated.');
      } else {
        await createMgaCrewRequest(periodId, payload);
        showSuccess('Deduction line added.');
      }
      closeForm();
      await mutateRequests();
      await onChanged?.();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? String(
              (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
            )
          : '';
      showError(msg || 'Could not save deduction line.');
    } finally {
      setBusy(false);
    }
  };

  const saveBonded = async () => {
    if (!canEdit || !editable) return;
    if (!bondedForm.description.trim()) {
      showError('Enter a description.');
      return;
    }
    if (isBondedExpense && bondedForm.payrollColumnCode && !bondedForm.mgaCrewLineId) {
      showError('Select a crew member to charge on payroll.');
      return;
    }
    const unitPrice = Number(bondedForm.unitPrice) || 0;
    const qty = Number(bondedForm.qty) || 0;
    setBusy(true);
    try {
      const payload = {
        section: isBondedExpense ? ('EXPENSE' as const) : ('STOCK' as const),
        entryDate: bondedForm.entryDate || null,
        description: bondedForm.description.trim(),
        unitPrice,
        qty,
        amount: unitPrice * qty,
        remarks: bondedForm.remarks.trim() || null,
        mgaCrewLineId:
          isBondedExpense && bondedForm.mgaCrewLineId
            ? Number(bondedForm.mgaCrewLineId)
            : null,
        payrollColumnCode:
          isBondedExpense && bondedForm.payrollColumnCode
            ? bondedForm.payrollColumnCode
            : null,
      };
      if (editingId != null) {
        await updateMgaBondedLine(periodId, editingId, payload);
        showSuccess(
          payload.payrollColumnCode
            ? 'Stock out updated (payroll charge synced).'
            : 'Bonded line updated.',
        );
      } else {
        await createMgaBondedLine(periodId, payload);
        showSuccess(
          payload.payrollColumnCode
            ? 'Stock out added (payroll charge created).'
            : 'Bonded line added.',
        );
      }
      closeForm();
      await mutateBonded();
      await mutateRequests();
      await onChanged?.();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? String(
              (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
            )
          : '';
      showError(msg || 'Could not save bonded line.');
    } finally {
      setBusy(false);
    }
  };

  const removeRequest = async (row: MgaCrewRequest) => {
    if (!canEdit || !editable) return;
    if (row.fromStockOut || row.mgaBondedLineId) {
      showError('This charge comes from Stock out. Remove it under Bonded store → Stock out.');
      return;
    }
    const ok = await confirm({
      title: 'Remove deduction line?',
      message: `${row.crewLine?.crewName || 'Crew'} · ${formatMoney(row.amount)} ${currency}`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMgaCrewRequest(periodId, row.id);
      showSuccess('Deduction line removed.');
      await mutateRequests();
      await onChanged?.();
    } catch {
      showError('Could not remove deduction line.');
    } finally {
      setBusy(false);
    }
  };

  const removeBonded = async (row: MgaBondedLine) => {
    if (!canEdit || !editable) return;
    const ok = await confirm({
      title: 'Remove bonded line?',
      message: `${row.description} · ${formatMoney(row.amount)} ${currency}`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMgaBondedLine(periodId, row.id);
                  showSuccess('Bonded line removed.');
      await mutateBonded();
      await mutateRequests();
      await onChanged?.();
    } catch {
      showError('Could not remove bonded line.');
    } finally {
      setBusy(false);
    }
  };

  const saveCash = async () => {
    if (!canEdit || !editable) return;
    if (!cashForm.description.trim()) {
      showError('Enter a description.');
      return;
    }
    const amount = Number(cashForm.amount);
    if (!Number.isFinite(amount)) {
      showError('Enter a valid amount.');
      return;
    }
    const entryType: MgaCashEntryType = isCashExpense
      ? 'EXPENSE'
      : cashForm.entryType === 'CTM'
        ? 'CTM'
        : 'INCOME';
    setBusy(true);
    try {
      const payload = {
        entryType,
        entryDate: cashForm.entryDate || null,
        description: cashForm.description.trim(),
        amount,
        currency: cashForm.currency || currency || 'USD',
        exchangeRate: Number(cashForm.exchangeRate) || 1,
        remarks: cashForm.remarks.trim() || null,
        mgaCrewLineId:
          entryType === 'EXPENSE' && cashForm.mgaCrewLineId
            ? Number(cashForm.mgaCrewLineId)
            : null,
      };
      if (editingId != null) {
        await updateMgaCashEntry(periodId, editingId, payload);
        showSuccess(
          entryType === 'EXPENSE' && cashForm.mgaCrewLineId
            ? 'Cash entry updated (Master adv. refreshed).'
            : 'Cash entry updated.',
        );
      } else {
        await createMgaCashEntry(periodId, payload);
        showSuccess(
          entryType === 'EXPENSE' && cashForm.mgaCrewLineId
            ? 'Cash entry added (Master adv. updated).'
            : 'Cash entry added.',
        );
      }
      closeForm();
      await mutateCash();
      await onChanged?.();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? String(
              (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
            )
          : '';
      showError(msg || 'Could not save cash entry.');
    } finally {
      setBusy(false);
    }
  };

  const removeCash = async (row: MgaCashEntry) => {
    if (!canEdit || !editable) return;
    const ok = await confirm({
      title: 'Remove cash entry?',
      message: `${row.description} · ${formatMoney(cashAmount(row))} ${row.currency || currency}`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMgaCashEntry(periodId, row.id);
      showSuccess('Cash entry removed.');
      await mutateCash();
      await onChanged?.();
    } catch {
      showError('Could not remove cash entry.');
    } finally {
      setBusy(false);
    }
  };

  const crewOptions = useMemo(
    () =>
      lines.map((l) => ({
        value: String(l.id),
        label: `${l.crewName}${l.rankName ? ` (${l.rankName})` : ''}`,
      })),
    [lines],
  );

  const renderRequestTable = useCallback(
    (pageList: MgaCrewRequest[], footerRows: MgaCrewRequest[]) => {
      const footerTotal = footerRows.reduce((s, r) => s + Number(r.amount || 0), 0);
      const footer =
        footerRows.length > 0 ? (
          <div
            className={cn(
              'sticky bottom-0 z-[1] mt-auto border-t-2',
              colors.border,
              colors.surface,
            )}
          >
            <table className="min-w-full text-sm">
              <tbody>
                <tr className="font-medium">
                  <td className="px-3 py-2" colSpan={2}>
                    Total
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(footerTotal)}</td>
                  <td colSpan={canEdit && editable ? 2 : 1} />
                </tr>
              </tbody>
            </table>
          </div>
        ) : null;
      return (
        <BooksTableShell footer={footer}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left', colors.border)}>
                <GeneralListSortableTh
                  sortKey="entryDate"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Date
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="crewName"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Crew
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="amount"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                  className="text-right"
                >
                  Amount
                </GeneralListSortableTh>
                <th className="px-3 py-2 font-medium">Remarks</th>
                {canEdit && editable ? <th className="px-3 py-2 w-24" /> : null}
              </tr>
            </thead>
            <tbody>
              {pageList.map((row) => {
                const fromStockOut = Boolean(row.fromStockOut || row.mgaBondedLineId);
                return (
                <tr key={row.id} className={cn('border-b', colors.border, colors.surfaceHover)}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.entryDate ? formatDateGlobal(String(row.entryDate).slice(0, 10)) : '—'}
                  </td>
                  <td className="px-3 py-2">{row.crewLine?.crewName || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.amount)}</td>
                  <td className={cn('px-3 py-2', colors.textMuted)}>
                    <span className="block">{row.remarks || '—'}</span>
                    {fromStockOut ? (
                      <span className={cn('mt-0.5 block text-[11px]', colors.textMuted)}>
                        From Stock out (edit there)
                      </span>
                    ) : null}
                  </td>
                  {canEdit && editable ? (
                    <td className="px-3 py-2">
                      {fromStockOut ? (
                        <span className={cn('block text-right text-[11px]', colors.textMuted)}>
                          Locked
                        </span>
                      ) : (
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditRequest(row)}
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600"
                          onClick={() => removeRequest(row)}
                          title="Remove"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                      )}
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
          </table>
        </BooksTableShell>
      );
    },
    [canEdit, editable, colors, sort],
  );

  const renderBondedTable = useCallback(
    (pageList: MgaBondedLine[], footerRows: MgaBondedLine[]) => {
      const footerTotal = footerRows.reduce((s, r) => s + Number(r.amount || 0), 0);
      const footer =
        footerRows.length > 0 ? (
          <div
            className={cn(
              'sticky bottom-0 z-[1] mt-auto border-t-2',
              colors.border,
              colors.surface,
            )}
          >
            <table className="min-w-full text-sm">
              <tbody>
                <tr className="font-medium">
                  <td className="px-3 py-2" colSpan={4}>
                    Total
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(footerTotal)}</td>
                  <td
                    colSpan={
                      (isBondedExpense ? 2 : 0) + (canEdit && editable ? 1 : 0)
                    }
                  />
                </tr>
              </tbody>
            </table>
          </div>
        ) : null;
      return (
        <BooksTableShell footer={footer}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left', colors.border)}>
                <GeneralListSortableTh
                  sortKey="entryDate"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Date
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="description"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Description
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="unitPrice"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                  className="text-right"
                >
                  Price
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="qty"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                  className="text-right"
                >
                  Qty
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="amount"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                  className="text-right"
                >
                  Amount
                </GeneralListSortableTh>
              {isBondedExpense ? (
                <GeneralListSortableTh
                  sortKey="crewName"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Crew
                </GeneralListSortableTh>
              ) : null}
              {isBondedExpense ? <th className="px-3 py-2 font-medium">Payroll</th> : null}
              {canEdit && editable ? <th className="px-3 py-2 w-24" /> : null}
              </tr>
            </thead>
            <tbody>
              {pageList.map((row) => (
                <tr key={row.id} className={cn('border-b', colors.border, colors.surfaceHover)}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.entryDate ? formatDateGlobal(String(row.entryDate).slice(0, 10)) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{row.description}</div>
                      {row.remarks ? (
                        <div className={cn('truncate text-xs', colors.textMuted)}>{row.remarks}</div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.unitPrice)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.qty)}</td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums font-medium',
                      isBondedExpense ? (isDark ? 'text-red-300' : 'text-red-700') : colors.text,
                    )}
                  >
                    {formatMoney(row.amount)}
                  </td>
                  {isBondedExpense ? (
                    <td className={cn('px-3 py-2', colors.textMuted)}>
                      {row.crewLine?.crewName || '—'}
                    </td>
                  ) : null}
                  {isBondedExpense ? (
                    <td className={cn('px-3 py-2 text-sm', colors.textMuted)}>
                      {isStoreOnlyStockOut(row) ? (
                        <span
                          className={cn(
                            'inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                            isDark ? 'bg-amber-500/15 text-amber-200' : 'bg-amber-50 text-amber-800',
                          )}
                          title="Not charged on payroll — store movement only"
                        >
                          Store only
                        </span>
                      ) : (
                        bondedPayrollColumnLabel(row.payrollColumnCode)
                      )}
                    </td>
                  ) : null}
                  {canEdit && editable ? (
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditBonded(row)}
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600"
                          onClick={() => removeBonded(row)}
                          title="Remove"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </BooksTableShell>
      );
    },
    [canEdit, editable, colors, sort, isBondedExpense, isDark],
  );

  const renderCashTable = useCallback(
    (pageList: MgaCashEntry[], footerRows: MgaCashEntry[]) => {
      const footerTotal = footerRows.reduce((s, r) => s + cashAmount(r), 0);
      const amountDanger = isCashExpense
        ? isDark
          ? 'text-red-300'
          : 'text-red-700'
        : colors.text;
      const footer =
        footerRows.length > 0 ? (
          <div
            className={cn(
              'sticky bottom-0 z-[1] mt-auto border-t-2',
              colors.border,
              colors.surface,
            )}
          >
            <table className="min-w-full text-sm">
              <tbody>
                <tr className="font-medium">
                  <td className="px-3 py-2" colSpan={isCashExpense ? 2 : 3}>
                    Total
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(footerTotal)}</td>
                  <td colSpan={(isCashExpense ? 1 : 0) + (canEdit && editable ? 1 : 0)} />
                </tr>
              </tbody>
            </table>
          </div>
        ) : null;
      return (
        <BooksTableShell footer={footer}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left', colors.border)}>
                <GeneralListSortableTh
                  sortKey="entryDate"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Date
                </GeneralListSortableTh>
                {!isCashExpense ? (
                  <GeneralListSortableTh
                    sortKey="entryType"
                    sortConfig={sort.sortConfig}
                    onSort={sort.handleSort}
                  >
                    Type
                  </GeneralListSortableTh>
                ) : null}
                <GeneralListSortableTh
                  sortKey="description"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                >
                  Description
                </GeneralListSortableTh>
                <GeneralListSortableTh
                  sortKey="amountPeriod"
                  sortConfig={sort.sortConfig}
                  onSort={sort.handleSort}
                  className="text-right"
                >
                  Amount
                </GeneralListSortableTh>
                {isCashExpense ? (
                  <GeneralListSortableTh
                    sortKey="crewName"
                    sortConfig={sort.sortConfig}
                    onSort={sort.handleSort}
                  >
                    Crew
                  </GeneralListSortableTh>
                ) : null}
                {canEdit && editable ? <th className="px-3 py-2 w-24" /> : null}
              </tr>
            </thead>
            <tbody>
              {pageList.map((row) => {
                const periodAmt = cashAmount(row);
                const rawAmt = Number(row.amount) || 0;
                const fx = Number(row.exchangeRate ?? 1) || 1;
                const foreign = Boolean(row.currency) && fx !== 1;
                const ccy = row.currency || '';
                return (
                  <tr key={row.id} className={cn('border-b', colors.border, colors.surfaceHover)}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.entryDate ? formatDateGlobal(String(row.entryDate).slice(0, 10)) : '—'}
                    </td>
                    {!isCashExpense ? (
                      <td className="px-3 py-2 whitespace-nowrap">{row.entryType}</td>
                    ) : null}
                    <td className="px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{row.description}</div>
                        {row.remarks ? (
                          <div className={cn('truncate text-xs', colors.textMuted)}>{row.remarks}</div>
                        ) : null}
                      </div>
                    </td>
                    <td className={cn('px-3 py-2 text-right tabular-nums font-medium', amountDanger)}>
                      <div>
                        {formatMoney(periodAmt)}
                        {ccy ? (
                          <span className={cn('ml-1 text-[11px] font-normal', colors.textMuted)}>
                            {ccy}
                          </span>
                        ) : null}
                      </div>
                      {foreign ? (
                        <div className={cn('text-[11px] font-normal tabular-nums', colors.textMuted)}>
                          {formatMoney(rawAmt)} {ccy} @ {fx}
                        </div>
                      ) : null}
                    </td>
                    {isCashExpense ? (
                      <td className={cn('px-3 py-2', colors.textMuted)}>
                        {row.crewLine?.crewName || '—'}
                      </td>
                    ) : null}
                    {canEdit && editable ? (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditCash(row)}
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={() => removeCash(row)}
                            title="Remove"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </BooksTableShell>
      );
    },
    [canEdit, editable, colors, sort, isCashExpense, isDark],
  );

  if (!selected) {
    return (
      <p className={cn('p-4 text-sm', colors.textMuted)}>
        No deduction list columns configured. Add sheet columns under System → MGA sheet columns.
      </p>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm lg:flex-row',
          colors.border,
          colors.surface,
        )}
      >
        <aside
          className={cn(
            'flex max-h-52 shrink-0 flex-col border-b lg:max-h-none lg:w-[15.5rem] lg:border-b-0 lg:border-r',
            colors.border,
            colors.headerBg,
          )}
        >
          <div className={cn('shrink-0 space-y-2 border-b px-3.5 py-2.5', colors.border)}>
            <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Categories
            </p>
            <TextInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search all books…"
              className="h-8 text-xs"
            />
            {search.trim() ? (
              <p className={cn('text-[11px]', colors.textMuted)}>
                {booksSearchHitTotal === 0
                  ? 'No matches across categories'
                  : `${booksSearchHitTotal} match${booksSearchHitTotal === 1 ? '' : 'es'} in ${booksSearchCategoryHits} categor${
                      booksSearchCategoryHits === 1 ? 'y' : 'ies'
                    }`}
              </p>
            ) : null}
          </div>
          <nav
            className="dashboard-scrollbar flex-1 overflow-y-auto px-1.5 py-2"
            aria-label="MGA book categories"
          >
            {sidebarGroups.map((group, groupIndex) => (
              <div key={group.id} className={cn(groupIndex > 0 && 'mt-2')}>
                <div
                  className={cn(
                    'sticky top-0 z-[1] px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider',
                    colors.headerBg,
                    colors.textMuted,
                  )}
                >
                  {group.title}
                </div>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const stats = categoryStats.get(item.code) ?? { count: 0, total: 0 };
                    const active = item.code === selected.code;
                    const matchCount = categoryMatchCounts.get(item.code) ?? 0;
                    const searching = search.trim().length > 0;
                    const dimmed = searching && matchCount === 0;
                    return (
                      <button
                        key={item.code}
                        ref={active ? activeItemRef : undefined}
                        type="button"
                        onClick={() => onCategoryChange(item.code)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'group flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
                          dimmed && 'opacity-40',
                          active
                            ? isDark
                              ? 'bg-indigo-500/20 text-indigo-100'
                              : 'bg-indigo-50 text-indigo-950'
                            : cn(colors.text, 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'),
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                            active
                              ? isDark
                                ? 'bg-indigo-300'
                                : 'bg-indigo-600'
                              : searching && matchCount > 0
                                ? isDark
                                  ? 'bg-emerald-400'
                                  : 'bg-emerald-500'
                                : isDark
                                  ? 'bg-slate-600'
                                  : 'bg-slate-300',
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[13px] font-medium leading-snug">
                              {item.name}
                            </span>
                            <span
                              className={cn(
                                'shrink-0 text-[11px] tabular-nums',
                                active
                                  ? isDark
                                    ? 'text-indigo-200/80'
                                    : 'text-indigo-700/80'
                                  : colors.textMuted,
                              )}
                            >
                              {searching ? matchCount : stats.count}
                            </span>
                          </span>
                          <span
                            className={cn(
                              'mt-0.5 block truncate text-[11px] tabular-nums leading-snug',
                              active
                                ? isDark
                                  ? 'text-indigo-200/70'
                                  : 'text-indigo-800/70'
                                : colors.textMuted,
                            )}
                          >
                            {searching
                              ? matchCount > 0
                                ? `${matchCount} match${matchCount === 1 ? '' : 'es'}`
                                : 'No matches'
                              : `${formatMoney(stats.total)}${currency ? ` ${currency}` : ''}`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {isBondedExpense && storeOnlyStockOuts.length > 0 ? (
            <div
              className={cn(
                'mx-3 mt-3 shrink-0 rounded-md border px-3 py-2 text-xs',
                isDark
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                  : 'border-amber-200 bg-amber-50 text-amber-900',
              )}
            >
              {storeOnlyStockOuts.length} Stock out line
              {storeOnlyStockOuts.length === 1 ? '' : 's'} marked{' '}
              <strong>Store only</strong> (no crew payroll charge). Edit the line to add crew +
              charge type if it should hit Cigarettes / Internet / Other.
            </div>
          ) : null}
          <GeneralList
            className="h-full min-h-0 [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none"
            density="compact"
            title={selected.name}
            description={
              <span className="tabular-nums">
                {listRowCount} line{listRowCount === 1 ? '' : 's'} · {formatMoney(categoryTotal)}
                {currency ? ` ${currency}` : ''}
                {selected.hint ? (
                  <span className={cn('mt-0.5 block font-normal normal-case tracking-normal', colors.textMuted)}>
                    {selected.hint}
                  </span>
                ) : null}
              </span>
            }
            sorting={{ sortConfig: sort.sortConfig, onSort: sort.handleSort }}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: isCashView
                ? 'Search description, type, crew, remarks…'
                : isBondedView
                  ? 'Search description, crew, remarks…'
                  : 'Search crew or remarks…',
            }}
            actions={
              canEdit && editable && (isCashView || isBondedView || lines.length > 0) ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    isCashView
                      ? openCashForm()
                      : isBondedView
                        ? openBondedForm()
                        : openRequestForm(selected.code)
                  }
                >
                  <PlusIcon className="h-4 w-4" />
                  Add line
                </Button>
              ) : null
            }
            hasFilters={hasSearch}
            onClearFilters={() => setSearch('')}
            clearFiltersLabel="Clear search"
            isLoading={isLoading}
            loadingLabel="Loading…"
            isEmpty={
              !isLoading &&
              (isCashView
                ? filteredCash.length === 0
                : isBondedView
                  ? filteredBonded.length === 0
                  : filteredRequests.length === 0)
            }
            emptyState={{
              icon: <UserIcon className="h-10 w-10" />,
              title: hasSearch
                ? 'No matching lines'
                : selected.code === CASH_INCOME_CODE
                  ? 'No Income / CTM yet'
                  : selected.code === CASH_EXPENSE_CODE
                    ? 'No Cash out yet'
                    : selected.code === BONDED_INCOMING_CODE
                      ? 'No Incoming stock yet'
                      : selected.code === BONDED_EXPENSE_CODE
                        ? 'No Stock out yet'
                        : `No ${selected.name} lines yet`,
              hint: hasSearch
                ? 'Try a different search.'
                : !isCashView && !isBondedView && lines.length === 0
                  ? 'Generate payroll crew lines first, then add Books charges here.'
                  : selected.hint
                    ? selected.hint
                    : canEdit && editable
                      ? `Add the first ${selected.name} line for this period.`
                      : undefined,
              action:
                canEdit &&
                editable &&
                (isCashView || isBondedView || lines.length > 0) &&
                !hasSearch ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      isCashView
                        ? openCashForm()
                        : isBondedView
                          ? openBondedForm()
                          : openRequestForm(selected.code)
                    }
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add {selected.name}
                  </Button>
                ) : undefined,
            }}
            pagination={
              activeFilteredCount > 0
                ? {
                    page,
                    totalPages,
                    total: activeFilteredCount,
                    itemsPerPage,
                    pageSizeOptions,
                    onPageChange: setPage,
                    onItemsPerPageChange: (size) => {
                      setItemsPerPage(size);
                      setPage(1);
                    },
                  }
                : undefined
            }
          >
            {isCashView
              ? renderCashTable(pageCashRows, filteredCash)
              : isBondedView
                ? renderBondedTable(pageBondedRows, filteredBonded)
                : renderRequestTable(pageRequestRows, filteredRequests)}
          </GeneralList>
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} size="sm">
        <ModalHeader>
          <ModalTitle>
            {editingId != null
              ? isCashView
                ? 'Edit cash entry'
                : isBondedView
                  ? 'Edit bonded line'
                  : 'Edit deduction line'
              : isCashView
                ? 'Add cash entry'
                : isBondedView
                  ? 'Add bonded line'
                  : 'Add deduction line'}
          </ModalTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Close"
            aria-label="Close"
            disabled={busy}
            onClick={closeForm}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </ModalHeader>
        <ModalContent>
          {isCashView ? (
            <div className="space-y-3">
              {!isCashExpense ? (
                <div>
                  <Label required>Type</Label>
                  <Select
                    value={cashForm.entryType === 'CTM' ? 'CTM' : 'INCOME'}
                    options={[
                      { value: 'INCOME', label: 'Income' },
                      { value: 'CTM', label: 'CTM' },
                    ]}
                    showClearButton={false}
                    onChange={(e) =>
                      setCashForm((f) => ({
                        ...f,
                        entryType: String(e.target.value) as MgaCashEntryType,
                      }))
                    }
                  />
                </div>
              ) : null}
              <div>
                <Label>Date</Label>
                <DateInput
                  value={cashForm.entryDate}
                  onChange={(e) => setCashForm((f) => ({ ...f, entryDate: e.target.value }))}
                />
              </div>
              <div>
                <Label required>Description</Label>
                <TextInput
                  value={cashForm.description}
                  onChange={(e) => setCashForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. Previous balance / remittance"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label required>Amount</Label>
                  <NumberInput
                    value={cashForm.amount}
                    onChange={(e) =>
                      setCashForm((f) => ({
                        ...f,
                        amount: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    allowDecimals
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <CurrencySelect
                    value={cashForm.currency}
                    includeIds={cashForm.currency ? [cashForm.currency] : undefined}
                    showClearButton={false}
                    onChange={(e) =>
                      setCashForm((f) => ({ ...f, currency: String(e.target.value || 'USD') }))
                    }
                  />
                </div>
              </div>
              <div>
                <Label>FX rate (→ period currency)</Label>
                <NumberInput
                  value={cashForm.exchangeRate}
                  onChange={(e) =>
                    setCashForm((f) => ({
                      ...f,
                      exchangeRate: e.target.value == null ? '1' : String(e.target.value),
                    }))
                  }
                  allowDecimals
                  showClearButton={false}
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Period amount ≈{' '}
                  {formatMoney((Number(cashForm.amount) || 0) * (Number(cashForm.exchangeRate) || 1))}
                </p>
              </div>
              {isCashExpense ? (
                <div>
                  <Label>Crew (optional)</Label>
                  <Select
                    value={cashForm.mgaCrewLineId}
                    options={[{ value: '', label: '— None —' }, ...crewOptions]}
                    showClearButton={false}
                    onChange={(e) =>
                      setCashForm((f) => ({ ...f, mgaCrewLineId: String(e.target.value) }))
                    }
                  />
                </div>
              ) : null}
              <div>
                <Label>Remarks</Label>
                <TextInput
                  value={cashForm.remarks}
                  onChange={(e) => setCashForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </div>
            </div>
          ) : isBondedView ? (
            <div className="space-y-3">
              <div>
                <Label>Date</Label>
                <DateInput
                  value={bondedForm.entryDate}
                  onChange={(e) => setBondedForm((f) => ({ ...f, entryDate: e.target.value }))}
                />
              </div>
              <div>
                <Label required>Description</Label>
                <TextInput
                  value={bondedForm.description}
                  onChange={(e) => setBondedForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Unit price</Label>
                  <NumberInput
                    value={bondedForm.unitPrice}
                    onChange={(e) =>
                      setBondedForm((f) => ({
                        ...f,
                        unitPrice: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    allowDecimals
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label>Qty</Label>
                  <NumberInput
                    value={bondedForm.qty}
                    onChange={(e) =>
                      setBondedForm((f) => ({
                        ...f,
                        qty: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    allowDecimals
                    showClearButton={false}
                  />
                </div>
              </div>
              {isBondedExpense ? (
                <>
                  <div>
                    <Label>Crew</Label>
                    <Select
                      value={bondedForm.mgaCrewLineId}
                      options={[{ value: '', label: '— None —' }, ...crewOptions]}
                      showClearButton={false}
                      onChange={(e) =>
                        setBondedForm((f) => ({
                          ...f,
                          mgaCrewLineId: String(e.target.value),
                          payrollColumnCode:
                            e.target.value && !f.payrollColumnCode
                              ? 'otherBondedAmount'
                              : e.target.value
                                ? f.payrollColumnCode
                                : '',
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Charge on payroll</Label>
                    <Select
                      value={bondedForm.payrollColumnCode}
                      options={[
                        { value: '', label: '— No payroll charge —' },
                        ...BONDED_PAYROLL_COLUMN_OPTIONS.map((o) => ({
                          value: o.value,
                          label: o.label,
                        })),
                      ]}
                      showClearButton={false}
                      onChange={(e) =>
                        setBondedForm((f) => ({
                          ...f,
                          payrollColumnCode: String(e.target.value),
                        }))
                      }
                    />
                    <p className={cn('mt-1 text-xs', colors.textMuted)}>
                      Creates/updates the matching Cigarettes / Internet / Other line for this crew.
                    </p>
                    {(!bondedForm.mgaCrewLineId || !bondedForm.payrollColumnCode) &&
                    (Number(bondedForm.unitPrice) > 0 || Number(bondedForm.qty) > 0) ? (
                      <p
                        className={cn(
                          'mt-2 rounded-md border px-2.5 py-2 text-xs',
                          isDark
                            ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                            : 'border-amber-200 bg-amber-50 text-amber-900',
                        )}
                      >
                        Without crew and a payroll charge, this Stock out is store-only and will not
                        update crew deductions.
                      </p>
                    ) : null}
                  </div>
                </>
              ) : null}
              <div>
                <Label>Remarks</Label>
                <TextInput
                  value={bondedForm.remarks}
                  onChange={(e) => setBondedForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label required>Category</Label>
                <Select
                  value={requestForm.columnCode}
                  options={requestCategories.map((c) => ({ value: c.code, label: c.name }))}
                  showClearButton={false}
                  onChange={(e) =>
                    setRequestForm((f) => ({ ...f, columnCode: String(e.target.value) }))
                  }
                />
              </div>
              <div>
                <Label>Date</Label>
                <DateInput
                  value={requestForm.entryDate}
                  onChange={(e) => setRequestForm((f) => ({ ...f, entryDate: e.target.value }))}
                />
              </div>
              <div>
                <Label required>Crew</Label>
                <Select
                  value={requestForm.mgaCrewLineId}
                  options={crewOptions}
                  showClearButton={false}
                  onChange={(e) =>
                    setRequestForm((f) => ({ ...f, mgaCrewLineId: String(e.target.value) }))
                  }
                />
              </div>
              <div>
                <Label required>Amount ({currency})</Label>
                <NumberInput
                  value={requestForm.amount}
                  onChange={(e) =>
                    setRequestForm((f) => ({
                      ...f,
                      amount: e.target.value == null ? '' : String(e.target.value),
                    }))
                  }
                  allowDecimals
                  showClearButton={false}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <TextInput
                  value={requestForm.remarks}
                  onChange={(e) => setRequestForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" disabled={busy} onClick={closeForm}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              busy ||
              !editable ||
              (isCashView
                ? !cashForm.description.trim() || !cashForm.amount
                : isBondedView
                  ? !bondedForm.description.trim()
                  : !requestForm.mgaCrewLineId || !requestForm.amount)
            }
            onClick={() =>
              isCashView ? saveCash() : isBondedView ? saveBonded() : saveRequest()
            }
          >
            {busy ? 'Saving…' : editingId != null ? 'Save changes' : 'Add line'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default BooksTab;

/** @deprecated Prefer BooksTab */
export const DeductionsTab = BooksTab;
